/* eslint-disable react/prop-types */

import React, { useEffect, useState } from "react";
import { Input } from "@nextui-org/react";
import { RangeCalendar } from "@nextui-org/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@nextui-org/popover";
import { CalendarDate } from "@internationalized/date";
import { SelectorCalendarIcon } from "./CustomDateInput";
import AppUtility from "../assets/js/AppUtility";

const CustomInputRangeCalendar = ({
  label,
  value,
  variant = "flat" | "underlined" | "faded" | "bordered" | null,
  isDisabled,
  error,
  errorMessage,
  onChange,
  name,
  minDate,
  hideLabel = false,
  width = "w-full",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formatValue, setFormatValue] = useState(null);

  const handleSelect = (dates) => {
    onChange?.({ target: { name, value: dates } });
    if (dates?.start && dates?.end) setIsOpen(false);
  };

  useEffect(() => {
    if (!value) {
      setFormatValue(null);
      return;
    }

    const startDate = convertToCalendarDate(value.start);
    const endDate = convertToCalendarDate(value.end);

    if (startDate && endDate) {
      setFormatValue({
        start: startDate,
        end: endDate,
      });
    } else if (startDate && !value.end) {
      setFormatValue({
        start: startDate,
        end: null,
      });
    } else {
      setFormatValue(null);
    }
  }, [value]);

  const convertToCalendarDate = (dateObj) => {
    if (!dateObj || !dateObj.year || !dateObj.month || !dateObj.day) {
      return null;
    }

    try {
      return new CalendarDate(dateObj.year, dateObj.month, dateObj.day);
    } catch (error) {
      return null;
    }
  };

  const convertToJSDate = (dateObj) => {
    if (!dateObj || !dateObj.year || !dateObj.month || !dateObj.day) {
      return null;
    }

    try {
      return new Date(dateObj.year, dateObj.month - 1, dateObj.day);
    } catch (error) {
      return null;
    }
  };

  const getDisplayValue = () => {
    if (!value) return "เลือกช่วงที่จัดกิจกรรม";

    const startJSDate = convertToJSDate(value.start);
    const endJSDate = convertToJSDate(value.end);

    if (startJSDate && endJSDate) {
      return `${AppUtility.displayDatetime(
        startJSDate
      )} - ${AppUtility.displayDatetime(endJSDate)}`;
    } else if (startJSDate) {
      return `${AppUtility.displayDatetime(startJSDate)} - เลือกวันสิ้นสุด`;
    } else {
      return "เลือกช่วงที่จัดกิจกรรม";
    }
  };

  const hasValidRange = value?.start && value?.end && formatValue?.start && formatValue?.end;
  const hasPartialSelection = value?.start && !value?.end && formatValue?.start;

  const getMinDate = (minDateDayjs) => {
    const year = Number(minDateDayjs.format("YYYY"));
    const month = Number(minDateDayjs.format("M"));
    const day = Number(minDateDayjs.format("D"));
    return new CalendarDate(year, month, day);
  };

  return (
    <Popover isOpen={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger>
        <Input
          label={hideLabel ? undefined : label}
          placeholder={hideLabel ? label : undefined}
          labelPlacement="outside"
          variant={variant ?? "underlined"}
          value={getDisplayValue()}
          isDisabled={isDisabled}
          isInvalid={error}
          readOnly
          startContent={
            <div className="cursor-pointer" onClick={() => setIsOpen(true)}>
              <SelectorCalendarIcon />
            </div>
          }
          classNames={{
            input: [
              "text-left",
              "cursor-pointer",
              !hasValidRange && !hasPartialSelection && "opacity-50",
            ],
            value: !hasValidRange && !hasPartialSelection && "text-black",
            inputWrapper: [
              "data-[hover=true]:bg-transparent",
              "transition-colors",
              "data-[invalid=true]:border-red-500",
              "group-data-[invalid=true]:border-red-500",
              "data-[invalid=true]:focus:border-red-500",
              "data-[invalid=true]:focus-within:border-red-500",
              variant === "bordered" &&
                "!border-1 !border-gray-300 shadow-none",
              isDisabled && [
                "!border-none",
                "bg-transparent",
                "!shadow-none",
                "!border-b-0",
              ],
            ],
            label: ["data-[invalid=true]:text-red-500"],
            base: ["data-[disabled=true]:opacity-100", width],
          }}
          onClick={() => setIsOpen(true)}
        />
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <RangeCalendar
          value={formatValue}
          onChange={handleSelect}
          locale="th-TH"
          minValue={ minDate ? getMinDate(minDate): undefined}
        />
      </PopoverContent>
      {error && <p className="text-red-500 text-sm pt-2">{errorMessage}</p>}
    </Popover>
  );
};

export default CustomInputRangeCalendar;
